import { SUB_DIGIT_UNICODE } from "@tradingApp/helpers/digitUnicode";
import { priceGetCurrencySymbol } from "@tradingApp/helpers/price/priceGetCurrencySymbol";

const MIN_FRACTION_DIGIT = 8;

interface PriceFormatWithZerosReductionParams {
  rawPrice: number;
  formattedPrice: string;
  currency?: string;
}

/**
 * * Replaces an excess number of zeros after a decimal point with subscript text. 0.000000001234 => 0.0₇1234
 * */
export function priceFormatWithZerosReduction(
  props: PriceFormatWithZerosReductionParams,
): string {
  const { rawPrice, formattedPrice, currency = "" } = props;

  if (!Number.isFinite(rawPrice) || Number.isInteger(rawPrice)) {
    return formattedPrice;
  }

  const { sign, integer, fractional, zeroQuantity } = getPriceByPart(rawPrice);

  if (!zeroQuantity) {
    return formattedPrice;
  }

  const currencySymbol = priceGetCurrencySymbol(currency);
  const currencyWithSpace = `\u00a0${currency}`;

  const formattedZeroQuantity = zeroQuantity
    .toString() // "14"
    .split("") // ["1", "4"]
    .map((item) => SUB_DIGIT_UNICODE[item]) // ["\u2081", "\u2084"]
    .join(""); // "\u2081\u2084"

  if (currencySymbol) {
    return `${sign}${currencySymbol}${integer}.0${formattedZeroQuantity}${fractional}`;
  }

  if (!currency) {
    return `${sign}${integer}.0${formattedZeroQuantity}${fractional}`;
  }

  return `${sign}${integer}.0${formattedZeroQuantity}${fractional}${currencyWithSpace}`;
}

interface Result {
  sign: string;
  integer: number;
  fractional: string;
  zeroQuantity: number | null;
}

const GET_PRICE_BY_PART_DEFAULT: Result = {
  sign: "",
  integer: 0,
  fractional: "0",
  zeroQuantity: null,
};

function getPriceByPart(price: number): Result {
  const absPrice = Math.abs(price);
  const integer = Math.trunc(absPrice);

  const preparedPrice =
    absPrice > 1 ? Number(`0.${String(absPrice).split(".")[1]}`) : absPrice;

  /**
   * * Get the exponent of a number
   * * Why Math.abs: Since we are dealing with a number less than 1, the exponent will be negative.
   * * Why Math.floor: Due to floating point issues, the Math.log10 method can return a fractional exponent. Math.log10(5.6785e-27)  => -26.245766369879796
   * */
  const exponent = Math.abs(Math.floor(Math.log10(preparedPrice)));

  /**
   * * Get the fractional part of a number
   * * We need to multiply our number by 10 raised to the exponent we got above  0.00000003200345 => 3.200345
   * * As a result, we get a number, the fractional part of which we reduce to 3 digit using toFixed.  3.200345 => 3.200
   * * Since the toFixed method returns a string, we need to convert it to a number in order to cut off extra zeros. 3.200 => 3.2
   * * Then remove the point and get our fractional part 3.2 => 32
   * */
  const fractional = String(
    +(preparedPrice * 10 ** exponent).toFixed(3),
  ).replace(".", "");

  const sign = price >= 0 ? "" : "-";

  const isPriceLongEnough =
    exponent - 1 + fractional.length >= MIN_FRACTION_DIGIT;

  if (!isPriceLongEnough) {
    return GET_PRICE_BY_PART_DEFAULT;
  }

  return {
    sign,
    integer,
    fractional,
    zeroQuantity: exponent - 2,
  };
}
